/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DROPDOWN_STYLE, IDropdownMenuItem } from "../../../../ui/components/DropdownMenu";
import { LayoutState_Column } from "../../../../ui/components/Layout/LayoutState";
import { useLayoutElement } from "../../../../ui/components/Layout/useLayoutElement";
import { ToolbarButton, ToolbarButtonProps } from "../../../../ui/components/Toolbar/ToolbarButton";
import { ToolbarCommandButton } from "../../../../ui/components/Toolbar/ToolbarCommandButton";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { VIEW_EDITOR_VIEW_MODE, ViewEditorView } from "../ViewEditorView";
import { useDeepCompareMemo } from "use-deep-compare";
import { useAbility } from "../../../../ui/hooks/useAbility";
import { ABILITY_ACTIONS } from "../../../../ability/AbilityService";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { useToggle, useTranslate } from "@adapptio/lib-components";
import { FC_ANCHOR } from "../../../../ui/components/FloatingContainer";
import {
	getPreviewResolutionById,
	previewResolutionsOptions,
	TPreviewResolutionId,
	usePreviewResolutionContext
} from "../../../../previewResolution";
import { FieldButton, FieldGroup } from "@adapptio/lib-editor-ui";
import { PreviewResolutionSettings } from "../../../../ui/components/PreviewResolutionSettings";
import { CMD } from "../../../../commands/CMD";

export interface IViewEditorMainToolbarProps {
	view: ViewEditorView;
}

const viewModeMenuItems: IDropdownMenuItem[] = [
	{
		id: VIEW_EDITOR_VIEW_MODE.DESIGN,
		label: "Design",
		icon: "mdi/pencil-ruler"
	},
	{
		id: VIEW_EDITOR_VIEW_MODE.PREVIEW,
		label: "Preview",
		icon: "mdi/eye"
	},
	{
		id: VIEW_EDITOR_VIEW_MODE.DEBUG,
		label: "Debug",
		icon: "mdi/bug"
	}
];

const PREVIEW_ID_BTN_STYLE: React.CSSProperties = { width: "130px" };
const PREVIEW_WIDTH_BTN_STYLE: React.CSSProperties = { width: "55px" };

export const ViewEditorMainToolbar: FC<IViewEditorMainToolbarProps> = ({ view }) => {
	const t = useTranslate();

	const previewBtnRef = useRef();
	const model = view.getViewModel();

	const previewResolutionCtx = usePreviewResolutionContext();

	const selectedResolution = getPreviewResolutionById(previewResolutionCtx?.previewResolutionId);

	const { previewWidth, safeAreaActive, toggleSafeArea } = previewResolutionCtx;

	const ability = useAbility();

	const layout = view.getLayout();

	// const baseClass = BEM("edt-module-view-editor-main-toolbar");

	const editSelection = useMemo(() => view.getViewModel().getSelection(), [ view ]);

	const panelLeft = useLayoutElement<LayoutState_Column>(layout.getById("panelLeft"));
	const panelRight = useLayoutElement<LayoutState_Column>(layout.getById("panelRight"));

	const [ resourceInfo, setResourceInfo ] = useState(() => ({
		resource: view.getResource(),
		parentList: view.getResourceParentList(),
		isBeingSaved: view.isBeingSaved()
	}));

	const [ viewInfo, setViewInfo ] = useState(view.getViewInfo());

	const [ viewMode, setViewMode ] = useState(() => view.getMode());

	const [ previewSettingsOpen, togglePreviewSettings, , closePreviewSettings ] = useToggle(false);

	const selectedViewModeMenuItem = useMemo(
		() => viewModeMenuItems.find((item) => item.id === viewMode),
		[ viewMode ]
	);

	const handleSelectViewMode = useCallback<ToolbarButtonProps["onMenuItemClick"]>((item) => {
		view.setMode(item.id as VIEW_EDITOR_VIEW_MODE);
	}, []);

	const saveAllowed = useDeepCompareMemo(() => view.canSave(), [ viewInfo, resourceInfo ]);

	const selectPreviewResolution = useCallback<ToolbarButtonProps["onMenuItemClick"]>(
		(resolution) => {
			previewResolutionCtx?.setPreviewResolutionId(resolution.id as TPreviewResolutionId);
		},
		[ previewResolutionCtx?.setPreviewResolutionId ]
	);

	useSimpleEvent(
		view.onModeChange,
		() => {
			setViewMode(view.getMode());
		},
		[]
	);

	const [ visible, setVisible ] = useState({
		undo: view.canUndo(),
		redo: view.canRedo()
	});

	useSimpleEvent(
		model.onRender,
		() => {
			setVisible({
				undo: view.canUndo(),
				redo: view.canRedo()
			});
		},
		[ model ]
	);

	useEffect(() => {
		const handleResourceInfoChange = () =>
			setResourceInfo({
				resource: view.getResource(),
				parentList: view.getResourceParentList(),
				isBeingSaved: view.isBeingSaved()
			});

		const handleViewInfoChange = () => {
			setViewInfo(view.getViewInfo());
			// setVisible({
			// 	undo: model.canUndo(),
			// 	redo: model.canRedo()
			// })
		};

		onEvent(view.onResourceInfoChanged, handleResourceInfoChange);
		onEvent(view.onViewInfoChanged, handleViewInfoChange);
		return () => {
			offEvent(view.onResourceInfoChanged, handleResourceInfoChange);
			offEvent(view.onViewInfoChanged, handleViewInfoChange);
		};
	}, [ view, editSelection ]);

	return (
		<>
			<ToolbarCommandButton
				icon="mdi/dock-left"
				active={panelLeft.isExpanded}
				commandId="viewEditor.edit.toggleLeftPanel"
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/undo-variant"
				disabled={!visible.undo}
				commandId={CMD.VIEW_EDITOR.EDIT.UNDO}
			/>

			<ToolbarCommandButton
				icon="mdi/redo-variant"
				disabled={!visible.redo}
				commandId={CMD.VIEW_EDITOR.EDIT.REDO}
			/>

			{/* <ToolbarButton
				className={baseClass.element("mode-select").toClassName()}
				icon={selectedViewModeMenuItem?.icon}
				label={selectedViewModeMenuItem?.label}
				menuItems={viewModeMenuItems}
				selectedMenuItemId={selectedViewModeMenuItem?.id}
				menuStyle={DROPDOWN_STYLE.COMPACT}
				onMenuItemClick={handleSelectViewMode}
			/> */}

			<ToolbarSpacer grow={1} />

			<ToolbarButton
				icon={selectedResolution.icon}
				label={t("editor", selectedResolution.label)}
				menuItems={previewResolutionsOptions}
				selectedMenuItemId={previewResolutionCtx?.previewResolutionId}
				menuStyle={DROPDOWN_STYLE.COMPACT}
				menuAnchor={FC_ANCHOR.LEFT_BOTTOM}
				onMenuItemClick={selectPreviewResolution}
				cssStyle={PREVIEW_ID_BTN_STYLE}
			/>

			<ToolbarButton
				ref={previewBtnRef}
				onClick={togglePreviewSettings}
				cssStyle={PREVIEW_WIDTH_BTN_STYLE}
			>
				<span>{previewWidth}px</span>
			</ToolbarButton>
			<PreviewResolutionSettings
				open={previewSettingsOpen}
				target={previewBtnRef}
				onClose={closePreviewSettings}
			/>

			<ToolbarSpacer />

			<FieldGroup>
				{viewModeMenuItems.map((item, index) => (
					<FieldButton
						key={index}
						icon={item.icon}
						tooltip={item.label}
						selected={item.id === selectedViewModeMenuItem.id}
						value={item.id}
						onClick={() => handleSelectViewMode(item)}
					/>
				))}
			</FieldGroup>

			<ToolbarSpacer />

			<ToolbarButton
				style={
					safeAreaActive && selectedViewModeMenuItem.id === VIEW_EDITOR_VIEW_MODE.DESIGN
						? "primary"
						: "default"
				}
				icon="mdi/select-all"
				tooltip="Toggle safe area"
				active={safeAreaActive && selectedViewModeMenuItem.id === VIEW_EDITOR_VIEW_MODE.DESIGN}
				disabled={selectedViewModeMenuItem.id !== VIEW_EDITOR_VIEW_MODE.DESIGN}
				onClick={toggleSafeArea}
			/>

			<ToolbarSpacer grow={1} />

			{/*<ToolbarNote className={baseClass.element("saved").toClassName()}>
				<DateTimeAgo
					date={resourceInfo.resource.metaData.modifiedAt}
					valueTerm={
						resourceInfo.isBeingSaved
							? termsEditorCommon.resourceInfo.saving
							: viewInfo.isModified
								? "Modified"
								: "Saved"
					}
					tooltipTerm={termsEditorCommon.resourceInfo.savedAtBy}
					tVars={{ by: resourceInfo.resource.metaData.modifiedBy }}
				/>
			</ToolbarNote>

			<ToolbarSpacer />*/}

			<ToolbarCommandButton
				icon="mdi/content-save"
				commandId="viewEditor.edit.saveResource"
				disabled={!saveAllowed}
				tooltip="Save"
			/>

			<ToolbarButton icon="mdi/backup-restore" disabled />

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/menu"
				tooltip="More options"
				menuItems={[
					{
						id: "rename",
						label: "Rename",
						icon: "mdi/pencil",
						commandId: "resource.rename",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.RENAME, resourceInfo.resource)
					},
					{
						id: "move",
						label: "Move",
						icon: "mdi/folder-move",
						commandId: "resource.moveDialog.open",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.RENAME, resourceInfo.resource)
					},
					{
						id: "duplicate",
						label: "Duplicate",
						icon: "mdi/content-copy",
						commandId: "resource.duplicate",
						commandArgs: [ resourceInfo.resource?.id, true ]
					},
					{
						id: "history",
						label: "Revision History",
						icon: "mdi/history",
						commandId: "resource.openRevisionHistory",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: true
					},
					{
						id: "export",
						label: "Export to YAML",
						icon: "mdi/export",
						commandId: "resource.export",
						commandArgs: [ resourceInfo.resource?.id ]
					},
					{
						id: "delete",
						label: "Delete",
						icon: "mdi/delete",
						commandId: "resource.delete",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.REMOVE, resourceInfo.resource)
					}
				]}
				menuAnchor={FC_ANCHOR.RIGHT_BOTTOM}
				menuStyle={DROPDOWN_STYLE.COMPACT}
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/dock-right"
				active={panelRight.isExpanded}
				commandId="viewEditor.edit.toggleRightPanel"
			/>
		</>
	);
};
